import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import AdminShell from "./AdminShell";
import { firestore, storage } from "../firebase";
import {
  collection,
  addDoc,
  deleteDoc,
  doc,
  getDocs,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";

const Grid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: 1fr 1fr;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 18px;
  padding: 14px;
  background: #fff;
`;

const Label = styled.div`
  font-weight: 900;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  padding: 12px;
  font-weight: 700;

  &:focus {
    outline: none;
    border-color: rgba(15, 118, 110, 0.45);
    box-shadow: 0 0 0 4px rgba(15, 118, 110, 0.12);
  }
`;

const Select = styled.select`
  width: 100%;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  padding: 12px;
  font-weight: 900;
  background: #fff;
  color: #111827;

  &:focus {
    outline: none;
    border-color: rgba(15, 118, 110, 0.45);
    box-shadow: 0 0 0 4px rgba(15, 118, 110, 0.12);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  padding: 12px;
  font-weight: 700;
  min-height: 110px;

  &:focus {
    outline: none;
    border-color: rgba(15, 118, 110, 0.45);
    box-shadow: 0 0 0 4px rgba(15, 118, 110, 0.12);
  }
`;

const BtnRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 10px;
`;

const Btn = styled.button`
  appearance: none;
  border: 1px solid rgba(15, 118, 110, 0.22);
  background: #0f766e;
  color: #fff;
  font-weight: 950;
  border-radius: 999px;
  padding: 10px 14px;
  cursor: pointer;

  &:disabled {
    opacity: 0.65;
    cursor: wait;
    transform: none;
  }

  &:hover {
    filter: brightness(1.03);
    transform: translateY(-1px);
  }
`;

const Ghost = styled(Btn)`
  background: #fff;
  color: #111827;
  border-color: #e5e7eb;

  &:hover {
    background: #f9fafb;
  }
`;

const Danger = styled(Btn)`
  border-color: rgba(233, 78, 27, 0.28);
  background: rgba(233, 78, 27, 0.08);
  color: #e94e1b;
`;

const Muted = styled.div`
  color: #6b7280;
  font-weight: 800;
  font-size: 13px;
`;

const Thumb = styled.img`
  width: 84px;
  height: 64px;
  object-fit: cover;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  background: #000;
`;

/* ✅ NEW: filter tabs */
const FilterRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin: 8px 0 14px;
`;

const FilterTab = styled.button`
  appearance: none;
  border-radius: 999px;
  padding: 10px 14px;
  font-weight: 950;
  cursor: pointer;
  border: 1px solid #e5e7eb;
  background: ${({ $active }) => ($active ? "#0f766e" : "#fff")};
  color: ${({ $active }) => ($active ? "#fff" : "#111827")};

  &:hover {
    background: ${({ $active }) => ($active ? "#0f766e" : "#f9fafb")};
    transform: translateY(-1px);
  }
`;

/* ---------- Helpers ---------- */

function makeStoragePath(file) {
  const safeName = (file?.name || "product")
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9.\-_]/g, "");
  return `products/${Date.now()}-${safeName}`;
}

const normalizeCat = (s) => String(s || "").trim();
const isCat = (a, b) => normalizeCat(a).toLowerCase() === normalizeCat(b).toLowerCase();

/* ---------- Component ---------- */

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  // ✅ NEW: categories (för dropdown + filter)
  const defaultCats = useMemo(
    () => ["Fik", "Utrustning", "Merch", "Presentkort", "Övrigt"],
    []
  );

  const [customCategory, setCustomCategory] = useState("");
  const [filterCategory, setFilterCategory] = useState("Alla"); // "Alla" eller kategori

  const [form, setForm] = useState({
    name: "",
    category: defaultCats[0], // ✅ NEW
    price: "",
    stock: "",
    description: "",
  });

  const [file, setFile] = useState(null);

  const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : ""), [file]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const load = async () => {
    const snap = await getDocs(collection(firestore, "products"));
    const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

    // stabil sort (nyast först om createdAt finns)
    list.sort((a, b) => {
      const at = a.createdAt?.toMillis?.() || 0;
      const bt = b.createdAt?.toMillis?.() || 0;
      if (bt !== at) return bt - at;
      return String(b.id).localeCompare(String(a.id));
    });

    setItems(list);

    // ✅ om filterCategory är en kategori som inte längre finns i listan, låt den ändå vara kvar
  };

  useEffect(() => {
    load();
  }, []);

  const categoriesFromData = useMemo(() => {
    const set = new Map();
    for (const it of items) {
      const c = normalizeCat(it.category);
      if (c) set.set(c.toLowerCase(), c);
    }
    return Array.from(set.values()).sort((a, b) => a.localeCompare(b, "sv"));
  }, [items]);

  const allCategories = useMemo(() => {
    // union: defaultCats + kategorier från data
    const map = new Map();
    defaultCats.forEach((c) => map.set(c.toLowerCase(), c));
    categoriesFromData.forEach((c) => map.set(c.toLowerCase(), c));
    return Array.from(map.values()).sort((a, b) => a.localeCompare(b, "sv"));
  }, [defaultCats, categoriesFromData]);

  const visibleItems = useMemo(() => {
    if (filterCategory === "Alla") return items;
    return items.filter((p) => isCat(p.category, filterCategory));
  }, [items, filterCategory]);

  const reset = () => {
    setForm({
      name: "",
      category: defaultCats[0],
      price: "",
      stock: "",
      description: "",
    });
    setCustomCategory("");
    setFile(null);
  };

  const uploadImageIfNeeded = async () => {
    if (!file) return { imageUrl: "", storagePath: "" };

    const path = makeStoragePath(file);
    const r = ref(storage, path);
    await uploadBytes(r, file);
    const url = await getDownloadURL(r);

    return { imageUrl: url, storagePath: path };
  };

  const getFinalCategory = () => {
    if (form.category === "__custom__") return normalizeCat(customCategory) || "Övrigt";
    return normalizeCat(form.category) || "Övrigt";
  };

  const add = async () => {
    if (!form.name.trim()) return alert("Skriv namn.");
    if (!file) return alert("Välj en produktbild att ladda upp.");
    if (form.category === "__custom__" && !customCategory.trim())
      return alert("Skriv en kategori (eller välj en i listan).");

    setBusy(true);
    try {
      const uploaded = await uploadImageIfNeeded();
      const categoryFinal = getFinalCategory();

      await addDoc(collection(firestore, "products"), {
        name: form.name.trim(),
        category: categoryFinal, // ✅ NEW
        price: Number(form.price) || 0,
        stock: Number(form.stock) || 0,
        description: (form.description || "").trim(),
        imageUrl: uploaded.imageUrl, // ✅ Download URL
        storagePath: uploaded.storagePath, // ✅ så vi kan radera filen
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      reset();
      await load();
      alert("Produkt sparad!");
    } catch (e) {
      alert(e?.message || "Kunde inte spara produkten.");
    } finally {
      setBusy(false);
    }
  };

  const updateField = async (id, patch) => {
    setBusy(true);
    try {
      await updateDoc(doc(firestore, "products", id), {
        ...patch,
        updatedAt: serverTimestamp(),
      });
      await load();
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm("Ta bort produkten?")) return;

    setBusy(true);
    try {
      // 1) ta bort doc
      await deleteDoc(doc(firestore, "products", p.id));

      // 2) ta bort bild om vi har storagePath
      if (p.storagePath) {
        try {
          await deleteObject(ref(storage, p.storagePath));
        } catch {
          // ignore
        }
      }

      await load();
    } catch (e) {
      alert(e?.message || "Kunde inte ta bort produkten.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminShell title="Produkter (upload)">
      <Grid>
        <Card>
          <h3 style={{ marginTop: 0 }}>Lägg till produkt</h3>

          <Label>Namn</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />

          <div style={{ height: 10 }} />

          {/* ✅ NEW: kategori */}
          <Label>Kategori</Label>
          <Select
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
          >
            {allCategories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
            <option value="__custom__">+ Ny kategori…</option>
          </Select>

          {form.category === "__custom__" ? (
            <>
              <div style={{ height: 10 }} />
              <Label>Ny kategori</Label>
              <Input
                value={customCategory}
                onChange={(e) => setCustomCategory(e.target.value)}
                placeholder="Ex: Dryck, Glass, Bollar…"
              />
            </>
          ) : null}

          <div style={{ height: 10 }} />

          <Label>Pris (SEK)</Label>
          <Input value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />

          <div style={{ height: 10 }} />

          <Label>Lager</Label>
          <Input value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} />

          <div style={{ height: 10 }} />

          <Label>Produktbild (upload)</Label>
          <Input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} />

          {previewUrl ? (
            <div style={{ marginTop: 10 }}>
              <Thumb src={previewUrl} alt="Preview" style={{ width: 220, height: 140 }} />
            </div>
          ) : (
            <Muted style={{ marginTop: 8 }}>Välj en bild att ladda upp.</Muted>
          )}

          <div style={{ height: 10 }} />

          <Label>Beskrivning</Label>
          <Textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />

          <BtnRow>
            <Btn type="button" onClick={add} disabled={busy}>
              {busy ? "Sparar…" : "Lägg till"}
            </Btn>
            <Ghost type="button" onClick={reset} disabled={busy}>
              Rensa
            </Ghost>
          </BtnRow>

          <Muted style={{ marginTop: 10 }}>
            Sparar i <code>products</code>: <b>name</b>, <b>category</b>, <b>price</b>, <b>stock</b>,{" "}
            <b>description</b>, <b>imageUrl</b> (+ <b>storagePath</b>).
          </Muted>
        </Card>

        <Card>
          <h3 style={{ marginTop: 0 }}>Befintliga produkter</h3>

          {/* ✅ NEW: filter per kategori */}
          <ReminderCategories
            categories={["Alla", ...allCategories]}
            active={filterCategory}
            setActive={setFilterCategory}
          />

          {visibleItems.length === 0 ? (
            <Muted>Inga produkter i denna kategori.</Muted>
          ) : (
            visibleItems.map((p) => (
              <div
                key={p.id}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 14,
                  padding: 12,
                  marginBottom: 10,
                  display: "grid",
                  gap: 8,
                }}
              >
                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  {p.imageUrl ? <Thumb src={p.imageUrl} alt={p.name} /> : null}
                  <div>
                    <div style={{ fontWeight: 950 }}>{p.name}</div>
                    <Muted>
                      {p.category ? <>Kategori: {p.category} • </> : null}
                      Pris: {Number(p.price || 0)} • Lager: {Number(p.stock || 0)}
                    </Muted>
                  </div>
                </div>

                <BtnRow>
                  <Btn
                    type="button"
                    onClick={() => updateField(p.id, { stock: Number(p.stock || 0) + 1 })}
                    disabled={busy}
                  >
                    +1 lager
                  </Btn>
                  <Btn
                    type="button"
                    onClick={() =>
                      updateField(p.id, { stock: Math.max(0, Number(p.stock || 0) - 1) })
                    }
                    disabled={busy}
                  >
                    -1 lager
                  </Btn>

                  {/* ✅ snabb ändring av kategori (valfritt men praktiskt) */}
                  <Ghost
                    type="button"
                    disabled={busy}
                    onClick={async () => {
                      const next = window.prompt("Ny kategori:", p.category || "");
                      if (!next) return;
                      await updateField(p.id, { category: next.trim() });
                    }}
                  >
                    Byt kategori
                  </Ghost>

                  <Danger type="button" onClick={() => remove(p)} disabled={busy}>
                    Ta bort
                  </Danger>
                </BtnRow>
              </div>
            ))
          )}
        </Card>
      </Grid>
    </AdminShell>
  );
}

/* ---------- Small helper component for category tabs ---------- */

function ReminderCategories({ categories, active, setActive }) {
  const Row = styled.div`
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    margin: 8px 0 14px;
  `;

  const Tab = styled.button`
    appearance: none;
    border-radius: 999px;
    padding: 10px 14px;
    font-weight: 950;
    cursor: pointer;
    border: 1px solid #e5e7eb;
    background: ${({ $active }) => ($active ? "#0f766e" : "#fff")};
    color: ${({ $active }) => ($active ? "#fff" : "#111827")};

    &:hover {
      background: ${({ $active }) => ($active ? "#0f766e" : "#f9fafb")};
      transform: translateY(-1px);
    }
  `;

  return (
    <Row>
      {categories.map((c) => (
        <Tab key={c} type="button" $active={active === c} onClick={() => setActive(c)}>
          {c}
        </Tab>
      ))}
    </Row>
  );
}

